import { runtimeVehicleDetailPath } from "../../clientViewRuntime/utils/routes";
import { extractMobilityIdFromPublicVehiclePath } from "../dealerListing/dealerListingPublicUrl";

const PRESERVED_QUERY_PARAM_PREFIXES = ["utm_"];
const PRESERVED_QUERY_PARAMS = new Set([
  "ref",
  "campaign",
  "compare",
  "gclid",
  "fbclid",
  "ttclid",
  "msclkid",
]);

const DROPPED_QUERY_PARAMS = new Set([
  "id",
  "listingId",
  "listing_id",
  "vehicleListingId",
  "vehicle_listing_id",
  "marketListingId",
  "market_listing_id",
  "mobilityListingId",
  "mobility_listing_id",
]);

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value == null || value === "") continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
}

function shouldPreserveQueryParam(key) {
  const normalized = String(key || "").trim();
  if (!normalized || DROPPED_QUERY_PARAMS.has(normalized)) return false;
  if (PRESERVED_QUERY_PARAMS.has(normalized)) return true;
  return PRESERVED_QUERY_PARAM_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * Build an in-app public vehicle detail path from a mobility listing id (M7).
 */
export function buildPublicVehicleUrl({
  mobilityListingId,
  locale = null,
  slug = "automap",
  manifest = null,
} = {}) {
  const id = String(mobilityListingId || "").trim();
  if (!id) return null;
  return runtimeVehicleDetailPath(slug, id, locale, manifest);
}

/**
 * Append approved attribution/navigation params to a canonical mobility URL.
 */
export function appendApprovedVehicleLinkParams(canonicalPublicUrl, params) {
  const basePath = String(canonicalPublicUrl || "").trim();
  if (!basePath) return null;

  const source =
    params instanceof URLSearchParams
      ? params
      : new URLSearchParams(params || undefined);

  const filtered = new URLSearchParams();
  for (const [key, value] of source.entries()) {
    if (!shouldPreserveQueryParam(key)) continue;
    if (value == null || value === "") continue;
    filtered.append(key, value);
  }

  const [path, existingQuery = ""] = basePath.split("?");
  const merged = new URLSearchParams(existingQuery);
  for (const [key, value] of filtered.entries()) {
    if (!merged.has(key)) merged.append(key, value);
  }
  const query = merged.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * Resolve canonical public vehicle URL from explicit identity metadata.
 */
export function resolveCanonicalVehiclePublicUrl({
  canonicalPublicUrl,
  mobilityListingId,
  locale = null,
  slug = "automap",
  manifest = null,
} = {}) {
  const mobilityId = String(mobilityListingId || "").trim() || null;
  const canonical = String(canonicalPublicUrl || "").trim() || null;

  if (canonical) {
    const segment = extractMobilityIdFromPublicVehiclePath(canonical);
    if (segment) {
      if (mobilityId && segment !== mobilityId) {
        return buildPublicVehicleUrl({ mobilityListingId: mobilityId, locale, slug, manifest });
      }
      return canonical;
    }
  }

  if (mobilityId) {
    return buildPublicVehicleUrl({ mobilityListingId: mobilityId, locale, slug, manifest });
  }

  return null;
}

/**
 * Normalize public listing API rows into explicit link identity fields.
 */
export function toPublicVehicleLinkInput(listing) {
  if (!listing || typeof listing !== "object") {
    return {
      canonicalPublicUrl: null,
      mobilityListingId: null,
      marketListingId: null,
      canonicalVehicleId: null,
    };
  }

  const marketListingId = firstNonEmpty(listing.marketListingId, listing.market_listing_id);
  const explicitMobility = firstNonEmpty(
    listing.mobilityListingId,
    listing.mobility_listing_id,
    listing.vehicleListingId,
    listing.vehicle_listing_id
  );
  const listingId = firstNonEmpty(listing.id, listing.listingId);

  let mobilityListingId = explicitMobility;
  if (!mobilityListingId && listingId) {
    if (!marketListingId || listingId !== marketListingId) {
      mobilityListingId = listingId;
    }
  }

  return {
    canonicalPublicUrl: firstNonEmpty(listing.canonicalPublicUrl, listing.canonical_public_url),
    mobilityListingId,
    marketListingId,
    canonicalVehicleId: firstNonEmpty(listing.canonicalVehicleId, listing.canonical_vehicle_id),
  };
}

export function resolvePublicVehicleLinkFromListing(listing, options = {}) {
  return resolveCanonicalVehiclePublicUrl({
    ...toPublicVehicleLinkInput(listing),
    ...options,
  });
}

/**
 * Stable public listing key for favorites, compare, and local storage.
 */
export function resolvePublicListingStorageId(listing) {
  return toPublicVehicleLinkInput(listing).mobilityListingId;
}

export function validatePublicVehicleUrl({
  publicUrl,
  mobilityListingId,
  marketListingId = null,
} = {}) {
  const url = String(publicUrl || "").trim();
  const mobilityId = String(mobilityListingId || "").trim();
  if (!url || !mobilityId) return false;
  if (!url.includes("/vehicles/")) return false;
  const segment = extractMobilityIdFromPublicVehiclePath(url);
  if (!segment || segment !== mobilityId) return false;
  if (marketListingId && segment === String(marketListingId).trim()) return false;
  return true;
}

export function buildPublicVehicleLinkAnalyticsFields(listing, extra = {}) {
  const identity = toPublicVehicleLinkInput(listing);
  const mobilityListingId = identity.mobilityListingId;
  return {
    listingId: mobilityListingId || extra.listingId || null,
    mobilityListingId,
    marketListingId: identity.marketListingId,
    canonicalVehicleId: identity.canonicalVehicleId,
    canonicalPublicUrl:
      identity.canonicalPublicUrl ||
      (mobilityListingId
        ? resolveCanonicalVehiclePublicUrl({ mobilityListingId, ...extra })
        : null),
    ...extra,
  };
}
